import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';

const BugTrendChart = ({ selectedApp, compact = false }) => {
  useAuth();
  const [trendData, setTrendData] = useState(() => {
    const cacheKey = `bugTrend_${selectedApp || 'global'}`;
    const cached = sessionStorage.getItem(cacheKey);
    return cached ? JSON.parse(cached) : [];
  });

  const [loading, setLoading] = useState(() => {
    const cacheKey = `bugTrend_${selectedApp || 'global'}`;
    const hasCache = !!sessionStorage.getItem(cacheKey);
    return !hasCache;
  });

  const fetchTrendData = useCallback(async () => {
    try {
      // Only set loading if we don't have cache
      const cacheKey = `bugTrend_${selectedApp || 'global'}`;
      if (!sessionStorage.getItem(cacheKey)) {
        setLoading(true);
      }

      const { data } = await axios.get(`/api/bugs/trend/30days?application=${selectedApp || ''}`);

      // Format data for display - show only last 30 days with better date formatting
      const formattedData = data.data.map(item => ({
        date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        fullDate: item.date,
        created: item.created || 0,
        fixed: item.fixed || 0,
        updated: item.updated || 0,
        rejected: item.rejected || 0,
        totalActivity: (item.created || 0) + (item.fixed || 0) + (item.updated || 0) + (item.rejected || 0)
      }));

      setTrendData(formattedData);
      sessionStorage.setItem(cacheKey, JSON.stringify(formattedData));
    } catch (error) {
      console.error('Failed to fetch bug activity trend:', error);
      // Don't show toast if we have cached data to avoid annoyance
      const cacheKey = `bugTrend_${selectedApp || 'global'}`;
      if (!sessionStorage.getItem(cacheKey)) {
        toast.error('Failed to fetch activity data');
      }
      setTrendData([]);
    } finally {
      setLoading(false);
    }
  }, [selectedApp]);

  useEffect(() => {
    fetchTrendData();
  }, [selectedApp, fetchTrendData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const totalActions = trendData.reduce((sum, item) => sum + item.totalActivity, 0);
  const hasData = totalActions > 0;

  if (!hasData) {
    return (
      <div className="flex flex-col items-center justify-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
        <p className="text-gray-500 font-medium text-xs">No activity recorded in the last 30 days</p>
        <p className="text-[10px] text-gray-400 mt-1">Activities like creation, status changes, and assignments will appear here.</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex gap-3">
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
            <span className="text-[10px] text-gray-600 font-medium">Created</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-green-500"></span>
            <span className="text-[10px] text-gray-600 font-medium">Fixed</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
            <span className="text-[10px] text-gray-600 font-medium">Updated</span>
          </div>
        </div>
        <p className="text-[10px] font-semibold text-gray-500">Total Activities: {totalActions}</p>
      </div>

      <ResponsiveContainer width="100%" height={compact ? 180 : 220}>
        <AreaChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
          <defs>
            <linearGradient id="colorCreated" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.1} />
              <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorFixed" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#10B981" stopOpacity={0.1} />
              <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorUpdated" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.1} />
              <stop offset="95%" stopColor="#F59E0B" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
          <XAxis
            dataKey="date"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 9, fill: '#6B7280' }}
            interval={Math.floor(trendData.length / 5)}
          />
          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 9, fill: '#6B7280' }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#fff',
              borderRadius: '6px',
              border: 'none',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              fontSize: '11px',
              fontWeight: 500,
              padding: '8px'
            }}
          />
          <Area
            type="monotone"
            dataKey="created"
            stroke="#3B82F6"
            strokeWidth={1.5}
            fillOpacity={1}
            fill="url(#colorCreated)"
            name="Created"
          />
          <Area
            type="monotone"
            dataKey="fixed"
            stroke="#10B981"
            strokeWidth={1.5}
            fillOpacity={1}
            fill="url(#colorFixed)"
            name="Fixed"
          />
          <Area
            type="monotone"
            dataKey="updated"
            stroke="#F59E0B"
            strokeWidth={1.5}
            fillOpacity={1}
            fill="url(#colorUpdated)"
            name="Updated"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BugTrendChart;
